import React from 'react';

import { T } from '../customer/customers.theme';

/**
 * The few marks this feature needs, drawn inline on a 24px grid so they
 * inherit colour and pull nothing over the network.
 */

const base = (size) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

export const TickIcon = ({ size = 28, width = 3 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M4 12.5l5.5 5.5L20 7" />
  </svg>
);

export const AlertIcon = ({ size = 26, width = 2.3 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5.5M12 16.4v.01" />
  </svg>
);

export const ClockIcon = ({ size = 26, width = 2.2 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5V12l3 2" />
  </svg>
);

export const InfoIcon = ({ size = 13, width = 1.9 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5.5M12 7.8v.01" />
  </svg>
);

export const CloseIcon = ({ size = 16, width = 2.2 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/**
 * A handset with the 1 key lit.
 *
 * The whole instruction, without words: this is what is about to happen on
 * your phone, and this is the key you press. Somebody who reads neither
 * Somali nor English still knows what to do, which at a till matters more
 * than the sentence underneath it.
 */
export const HandsetIcon = ({ width = 92 }) => {
  const scale = width / 92;
  const key = (label, hot) => (
    <span
      key={label}
      style={{
        width: 16 * scale,
        height: 14 * scale,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 3 * scale,
        background: hot ? T.brand : T.hover,
        color: hot ? T.surface : T.inkFaint,
        fontSize: 7.5 * scale,
        fontWeight: 800,
        boxShadow: hot ? `0 0 0 ${3 * scale}px ${T.brandTint}` : 'none',
      }}
    >
      {label}
    </span>
  );

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'relative',
        width,
        height: 160 * scale,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 5 * scale,
        padding: `${18 * scale}px ${8 * scale}px 0`,
        border: `${2.5 * scale}px solid ${T.ink}`,
        borderRadius: 15 * scale,
        background: T.surface,
        boxSizing: 'border-box',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 7 * scale,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 26 * scale,
          height: 3 * scale,
          borderRadius: 2 * scale,
          background: T.ink,
        }}
      />
      <span
        style={{
          width: '100%',
          padding: `${6 * scale}px ${5 * scale}px`,
          background: T.sunk,
          border: `1px solid ${T.border}`,
          borderRadius: 6 * scale,
          fontSize: 8 * scale,
          lineHeight: 1.35,
          color: T.inkSoft,
          textAlign: 'left',
        }}
      >
        Waafi
        <br />
        Enter PIN
      </span>
      <span style={{ display: 'flex', gap: 4 * scale }}>
        {[['1', true], ['2', false], ['3', false]].map(([l, hot]) => key(l, hot))}
      </span>
      <span style={{ display: 'flex', gap: 4 * scale }}>
        {[['4'], ['5'], ['6']].map(([l]) => key(l, false))}
      </span>
      <span style={{ display: 'flex', gap: 4 * scale }}>
        {[['7'], ['8'], ['9']].map(([l]) => key(l, false))}
      </span>
    </div>
  );
};

/**
 * Three dots, honest about where things are.
 *
 * Not a progress bar: the server reports no percentage and sends no updates,
 * it answers once when it is over. A bar that crept along would be inventing
 * information about somebody's money.
 */
export const StepDots = ({ step = 2 }) => {
  const dot = (index, label) => {
    const done = index < step;
    const now = index === step;
    return (
      <span
        style={{
          width: 22,
          height: 22,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: T.radiusPill,
          fontSize: 10,
          fontWeight: 800,
          background: done ? T.paid : now ? T.brand : T.hover,
          color: done || now ? T.surface : T.inkFaint,
          boxShadow: now ? `0 0 0 4px ${T.brandTint}` : 'none',
        }}
      >
        {done ? '✓' : label}
      </span>
    );
  };

  const bar = (index) => (
    <span
      style={{ width: 34, height: 2, background: index < step ? T.paid : T.border }}
    />
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center' }} aria-hidden="true">
      {dot(1, '1')}
      {bar(1)}
      {dot(2, '2')}
      {bar(2)}
      {dot(3, '3')}
    </div>
  );
};

/** The round tinted badge behind every outcome mark. */
export const Seal = ({ tone = 'ok', children }) => {
  const tones = {
    ok: { bg: T.paidTint, ink: T.paid },
    bad: { bg: T.dangerTint, ink: T.danger },
    warn: { bg: T.pendingTint, ink: T.pendingInk },
  };
  const { bg, ink } = tones[tone] || tones.ok;
  return (
    <span
      style={{
        width: 64,
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: T.radiusPill,
        background: bg,
        color: ink,
      }}
    >
      {children}
    </span>
  );
};
